import React, { useEffect, useState } from "react";
import Button from "../../component/button/Button";
import { IoIosArrowDown } from "react-icons/io";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import style from "../userInvite/UserInvite.module.css";
import InputText from "../../component/inputText/InputText";
import {
  userCreate,
  userShow,
  userUpdate,
  getSubscriptionPlans,
  userCancelPremium,
} from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

const UserInvite = ({
  isOpen,
  onClose,
  handleEditId,
  setHandleEditId,
  onInviteSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    user_type: "",
    plan_price_id: "",
  });

  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [planPriceOptions, setPlanPriceOptions] = useState([]);
  const [loadingPlanPrices, setLoadingPlanPrices] = useState(false);
  // Premium status the user had when the modal opened. Drives whether the
  // "Cancel Premium" button shows — only for an already-Premium user in edit mode.
  const [wasPremium, setWasPremium] = useState(false);

  const userCategoryOptions = [
    { label: "Premium", value: "Premium" },
    { label: "Standard", value: "Standard" },
  ];

  // Premium granted from here is an admin bypass: the backend creates the
  // subscription directly (platform 'admin', no Stripe, no payment) using the
  // selected plan price for the expiry math — used for demo/comp accounts.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoadingPlanPrices(true);
      try {
        const res = await getSubscriptionPlans({ page: 1, limit: 100, status: 1 });
        const plans = res?.subscription_plans || res?.data?.subscription_plans || [];
        // Zero-price (Free) plans are excluded: Standard users get the Free
        // plan auto-attached by the backend, so it's never a Premium grant.
        const options = plans.flatMap((plan) =>
          (plan.prices || [])
            .filter((p) => p.status === "Active" && Number(p.price) > 0)
            .map((p) => ({
              label: `${plan.name} — ${p.currency || "USD"} ${Number(p.price).toFixed(2)} / ${p.billing_cycle}`,
              value: p.id,
            }))
        );
        setPlanPriceOptions(options);
      } catch (err) {
        showToast("Failed to load plans", "error");
      } finally {
        setLoadingPlanPrices(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!handleEditId) {
      setWasPremium(false);
      setFormData({
        name: "",
        email: "",
        password: "",
        password_confirmation: "",
        user_type: "",
        plan_price_id: "",
      });
      return;
    }
    (async () => {
      try {
        const res = await userShow(null, handleEditId);
        const data = res.data?.data;
        let validUserType = ["Standard", "Premium"].includes(data.user_type)
          ? data.user_type
          : "Standard";

        setWasPremium(validUserType === "Premium");
        setFormData({
          name: data.name || "",
          email: data.email || "",
          password: "",
          password_confirmation: "",
          user_type: validUserType,
          plan_price_id: data.plan_price_id || "",
        });
      } catch (err) {
        showToast("Failed to fetch user details", "error");
      }
    })();
  }, [isOpen, handleEditId]);

  const handleCancelPremium = async () => {
    if (!handleEditId) return;
    const confirmed = window.confirm(
      "Cancel this user's Premium access?\n\nThis takes effect immediately — the user loses Premium access right away and is moved back to Standard/Free. This cannot be undone from here."
    );
    if (!confirmed) return;
    setCancelling(true);
    try {
      const response = await userCancelPremium(handleEditId);
      showToast(
        response?.data?.message || "Premium cancelled",
        "success"
      );
      if (onInviteSuccess) onInviteSuccess();
      setTimeout(() => {
        setHandleEditId(null);
        onClose();
      }, 500);
    } catch (error) {
      const resp = error?.response?.data;
      showToast(
        Object.values(resp?.errors || {}).flat()?.[0] ||
          resp?.message ||
          "Failed to cancel Premium",
        "error"
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "user_type" && value !== "Premium") {
      setFormData((prev) => ({ ...prev, user_type: value, plan_price_id: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();

    if (formData.user_type === "Premium" && !formData.plan_price_id) {
      showToast("Please select a plan for the Premium user", "error");
      return;
    }

    setLoading(true);

    try {
      let response;
      if (handleEditId) {
        response = await userUpdate(formData, handleEditId);
      } else {
        response = await userCreate(formData);
      }

      showToast(response.data.message, "success");

      if (onInviteSuccess) onInviteSuccess();

      setTimeout(() => {
        setFormData({
          name: "",
          email: "",
          password: "",
          password_confirmation: "",
          user_type: "",
          plan_price_id: "",
        });
        setHandleEditId(null);
        onClose();
      }, 500);
    } catch (error) {
      const response = error?.response?.data;
      const errorMessage =
        Object.values(response?.errors || {}).flat()?.[0] ||
        response?.message ||
        "An error occurred";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={style.overlay} onClick={handleOverlayClick}>
        <div className={style.modal}>
          <div className={style.headingText}>
            {handleEditId ? "Edit User" : "User Invite"}
          </div>
          <form onSubmit={handleSendInvite}>
            <div className={style.inputBox}>
              <InputText
                name="name"
                value={formData.name}
                placeholder="Full Name"
                type="text"
                variant="modal"
                className={style.inputText}
                onChange={handleChange}
              />
              <InputText
                name="email"
                value={formData.email}
                placeholder="Email Address"
                type="text"
                variant="modal"
                className={style.inputText}
                onChange={handleChange}
              />
              <div className={style.passwordWrapper}>
                <InputText
                  name="password"
                  value={formData.password}
                  placeholder="password"
                  type={showPassword ? "text" : "password"}
                  variant="modal"
                  className={style.inputText}
                  onChange={handleChange}
                  showToggle={false}
                />
                <span
                  className={style.eyeIcon}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className={style.passwordWrapper}>
                <InputText
                  name="password_confirmation"
                  value={formData.password_confirmation}
                  placeholder="password_confirmation"
                  type={showPasswordConfirmation ? "text" : "password"}
                  variant="modal"
                  className={style.inputText}
                  onChange={handleChange}
                  showToggle={false}
                />
                <span
                  className={style.eyeIcon}
                  onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                >
                  {showPasswordConfirmation ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
              <div className={style.selectRow}>
                <Button
                  iconRight={<IoIosArrowDown className={style.arrowIcon} />}
                  name="user_type"
                  type="select"
                  options={userCategoryOptions}
                  selectLabel="Select User Type"
                  value={formData.user_type}
                  onChange={handleChange}
                />
              </div>
              {formData.user_type === "Premium" && (
                <div className={style.selectRow}>
                  <Button
                    iconRight={<IoIosArrowDown className={style.arrowIcon} />}
                    name="plan_price_id"
                    type="select"
                    options={planPriceOptions}
                    selectLabel={
                      loadingPlanPrices ? "Loading plans..." : "Select Plan"
                    }
                    value={formData.plan_price_id}
                    onChange={handleChange}
                    disabled={loadingPlanPrices}
                  />
                </div>
              )}
            </div>

            <div className={style.sendBtn}>
              {handleEditId && wasPremium && (
                <Button
                  className={style.cancelPremiumBtn}
                  type="button"
                  variant="outline"
                  loading={cancelling}
                  onClick={handleCancelPremium}
                >
                  Cancel Premium
                </Button>
              )}
              <Button
                className={style.inviteText}
                type="submit"
                loading={loading}
                style={{ background: loading ? "none" : "#b87333" }}
              >
                {handleEditId !== null ? "Update User" : "Add User"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UserInvite;
